import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { Phone, Star, Clock, ShieldCheck, ArrowDown } from "lucide-react";
import { StaggerLines } from "../Reveal";
import { OrderForm } from "../OrderForm";
import { PHONE, PHONE_HREF, IMAGES } from "../../lib/site";

const Stat = ({ value, label }) => (
  <div>
    <div className="font-display font-black text-3xl md:text-4xl text-[#0b192c]">{value}</div>
    <div className="text-xs md:text-sm text-slate-500 mt-0.5">{label}</div>
  </div>
);

export const Hero = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], reduce ? [0, 0] : [0, 120]);
  const imgScale = useTransform(scrollYProgress, [0, 1], reduce ? [1, 1] : [1, 1.12]);

  return (
    <section ref={ref} className="relative bg-white dc-grid pt-14 md:pt-20 pb-16 overflow-hidden" data-testid="hero">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-8 items-start">
          {/* Left: copy */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="inline-flex items-center gap-2 rounded-full bg-[#0b192c] text-white px-4 py-1.5 text-xs font-semibold tracking-wide"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#ef233c] animate-pulse" />
              Trusted DC passport & visa experts · 20+ years
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.25, duration: 0.6 }}
              className="mt-4 flex items-center gap-2.5 text-sm"
              data-testid="hero-rating"
            >
              <div className="flex">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} className="w-4 h-4 fill-[#f5b301] text-[#f5b301]" />
                ))}
              </div>
              <span className="font-semibold text-[#0b192c]">4.9</span>
              <span className="text-slate-500">Google &amp; Yelp reviews</span>
            </motion.div>

            <h1 className="mt-5 font-display font-black text-[#0b192c] text-[30px] leading-[1.08] sm:text-[40px] lg:text-[48px] tracking-tight">
              <StaggerLines
                lines={["Need Your", <>Passport <span className="text-[#d90429]">Fast?</span></>, "Expedited Passport & Visa"]}
              />
              <span className="block overflow-hidden">
                <motion.span
                  className="block text-slate-400"
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 0.9, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
                >
                  Services in Washington DC
                </motion.span>
              </span>
            </h1>

            <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
              Helping travelers quickly obtain passports, visas, apostilles, and travel documents with expert guidance and personalized service — right in the heart of Washington DC.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a href="#quote" className="inline-flex items-center gap-2 rounded-full bg-[#d90429] text-white px-7 py-4 font-semibold text-base hover:bg-[#ef233c] transition-colors" data-testid="hero-cta-primary">
                Get Started Today <ArrowDown className="w-4 h-4" />
              </a>
              <a href={PHONE_HREF} className="inline-flex items-center gap-2 rounded-full border-2 border-[#0b192c] text-[#0b192c] px-7 py-[14px] font-semibold text-base hover:bg-[#0b192c] hover:text-white transition-colors" data-testid="hero-cta-call">
                <Phone className="w-4 h-4" /> Call Now
              </a>
            </div>

            <div className="mt-9 grid grid-cols-3 gap-6 max-w-lg border-t border-slate-200 pt-8">
              <Stat value="20+" label="Years experience" />
              <Stat value="78" label="Countries served" />
              <Stat value="5 Days" label="Expedited options" />
            </div>

            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-600">
              <span className="inline-flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#d90429]" /> 20+ years serving travelers</span>
              <span className="inline-flex items-center gap-2"><Clock className="w-4 h-4 text-[#d90429]" /> Expedited & emergency options</span>
            </div>
            <p className="mt-4 text-xs text-slate-500 max-w-xl leading-relaxed">
              A private courier service — not a government agency, and not affiliated with or endorsed by the U.S. government. Processing times are estimates, not guarantees.
            </p>
          </div>

          {/* Right: image + form */}
          <div className="lg:col-span-5 relative" id="quote">
            <div className="relative rounded-2xl overflow-hidden h-40 sm:h-48 md:h-56 mb-5 border border-black/5 shadow-[0_8px_30px_rgba(11,25,44,0.12)]">
              <motion.img
                style={{ y: imgY, scale: imgScale }}
                src={IMAGES.traveler}
                alt="Business traveler checking flight schedule at the airport"
                className="w-full h-[130%] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0b192c]/60 to-transparent" />
              <div className="absolute bottom-4 left-4 text-white">
                <p className="text-xs uppercase tracking-widest text-white/80">Departing soon?</p>
                <p className="font-display font-bold text-xl">We work in urgency.</p>
              </div>
            </div>
            <OrderForm sourcePage="hero" />
          </div>
        </div>
      </div>
    </section>
  );
};
